<template>
  <v-dialog
    v-model="dialog"
    width="640"
    :content-class="
      is_mobile
        ? 'card pa-0 deelnemers-dialog'
        : 'card px-0 pb-0 pt-16 deelnemers-dialog'
    "
  >
    <!-- Buiten de scrollregio, zodat het kruisje in beeld blijft terwijl de
         datums eronder scrollen. De bovenrand met het kruisje staat vast; de
         overige padding zit op de scrollregio. -->
    <v-btn
      icon
      dark
      :class="is_mobile ? 'ml-auto mt-4 mr-4' : 'dialog-close-btn'"
      variant="text"
      :aria-label="$t('deelnemers.close')"
      @click="$emit('close-dialog')"
    >
      <v-icon>mdi-close</v-icon>
    </v-btn>

    <div
      class="deelnemers-dialog__scroll d-flex flex-column"
      :class="is_mobile ? 'px-6 pb-6' : 'px-16 pb-16'"
    >
      <h2 :class="is_mobile ? 'mb-4' : 'mb-8'">{{ $t("deelnemers.title") }}</h2>

      <div v-if="loading" class="deelnemers-dialog__state">
        <v-progress-circular
          indeterminate
          size="28"
          width="3"
          color="var(--company-primary)"
        ></v-progress-circular>
      </div>

      <p v-else-if="dateGroups.length === 0" class="deelnemers-dialog__state">
        {{ $t("deelnemers.empty") }}
      </p>

      <div v-else class="deelnemers-accordion">
        <div
          v-for="group in dateGroups"
          :key="group.date_id"
          class="deelnemers-accordion__item"
        >
          <button
            type="button"
            class="deelnemers-accordion__header"
            :aria-expanded="String(isOpen(group.date_id))"
            @click="toggle(group.date_id)"
          >
            <span class="deelnemers-accordion__date">
              {{ formatDate(group.date) }}
            </span>
            <v-icon
              class="deelnemers-accordion__chevron"
              :class="{ 'deelnemers-accordion__chevron--open': isOpen(group.date_id) }"
            >
              mdi-chevron-down
            </v-icon>
          </button>

          <div v-if="isOpen(group.date_id)" class="deelnemers-accordion__body">
            <div
              v-for="slot in group.slots"
              :key="slot.slot_id"
              class="deelnemers-slot"
            >
              <div class="deelnemers-slot__head">
                <span class="deelnemers-slot__meta">
                  <v-icon size="20">mdi-clock-outline</v-icon>
                  <span>{{ slot.start }}&ndash;{{ slot.end }}</span>
                </span>
                <span v-if="slot.location_title" class="deelnemers-slot__meta">
                  <v-icon size="20">mdi-map-marker-outline</v-icon>
                  <span>{{ slot.location_title }}</span>
                </span>
              </div>

              <ul class="deelnemers-list">
                <li
                  v-for="person in slot.participants"
                  :key="person.user_id"
                  class="deelnemers-list__row"
                >
                  <participant-avatar :person="person" :size="40" />
                  <span class="deelnemers-list__name" :title="fullName(person)">
                    {{ fullName(person) }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div :class="is_mobile ? 'mt-6' : 'mt-8'">
        <!-- Geen :block op mobiel: het ontwerp houdt "Sluiten" ook daar een
             smalle pil, links uitgelijnd met de dialoograand. -->
        <v-btn
          class="btn quickfix-btn"
          color="var(--company-primary)"
          variant="outlined"
          @click="$emit('close-dialog')"
        >
          {{ $t("deelnemers.close") }}
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import isMobile from "@/mixins/isMobile";
import ParticipantAvatar from "@/components/participant-avatar.vue";
import { fullNameFor } from "@/util/participants";
import { toAmsterdamCalendarDate } from "@/util/DateUtil";

/**
 * De Deelnemers-popup — één component voor de activiteitenpagina en het
 * dashboard, zodat beide oppervlakken dezelfde lijst tonen (ADR-0059).
 *
 * Krijgt de payload van `GET /v1/activities/{id}/participants` als prop; de
 * aanroeper haalt hem op, want de activiteitenpagina heeft hem toch al nodig
 * voor de avatarrij en zou hem anders twee keer ophalen.
 */
export default {
  name: "DeelnemersDialog",

  components: { ParticipantAvatar },

  mixins: [isMobile],

  props: {
    modelValue: { type: Boolean, default: false },
    dates: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    // Welke datum opengeklapt begint. De Datumkiezer opent de popup vanaf één
    // tijdslotkaart en wijst dan díe datum aan (ADR-0060); zonder waarde valt
    // hij terug op de eerste.
    initialDateId: { type: [Number, String], default: null },
  },

  emits: ["update:modelValue", "close-dialog"],

  data: () => ({
    // Which date is unfolded. One at a time, as designed — with every date open
    // the dialog is a wall of faces and the dates stop being scannable.
    openDateId: null,
    touched: false,
  }),

  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },

    dateGroups() {
      return (this.dates || []).filter(
        (group) => (group.slots || []).length > 0,
      );
    },

    // Altijd de date_id uit de payload zelf teruggeven, nooit de prop: die kan
    // als string binnenkomen en `isOpen` vergelijkt met ===.
    defaultDateId() {
      const groups = this.dateGroups;
      if (groups.length === 0) return null;

      const match = groups.find(
        (group) => String(group.date_id) === String(this.initialDateId),
      );

      return match ? match.date_id : groups[0].date_id;
    },
  },

  watch: {
    // Reopen on the first date whenever a fresh payload arrives, but never
    // yank a date shut that the reader just opened.
    dateGroups: {
      immediate: true,
      handler() {
        if (this.touched) return;
        this.openDateId = this.defaultDateId;
      },
    },

    // Een klik op een andere tijdslotkaart wijst een nieuwe startdatum aan.
    // Die hoort te winnen van wat de lezer bij een vorige opening uitklapte —
    // anders opent de popup op de datum van de vorige kaart.
    initialDateId() {
      this.touched = false;
      this.openDateId = this.defaultDateId;
    },

    modelValue(open) {
      if (!open) {
        this.touched = false;
        this.openDateId = this.defaultDateId;
      }
    },
  },

  methods: {
    fullName(person) {
      return fullNameFor(person);
    },

    isOpen(dateId) {
      return this.openDateId === dateId;
    },

    toggle(dateId) {
      this.touched = true;
      this.openDateId = this.isOpen(dateId) ? null : dateId;
    },

    /**
     * "25 September 2025". The month is capitalised to match the design, which
     * is what this app does elsewhere too, even though Dutch would lowercase it.
     */
    formatDate(epochSeconds) {
      if (!epochSeconds) return "";
      const locale = this.$i18n.locale === "en" ? "en-GB" : "nl-NL";
      const text = toAmsterdamCalendarDate(epochSeconds).toLocaleDateString(
        locale,
        {
        day: "numeric",
        month: "long",
        year: "numeric",
      });

      return text.replace(
        /\p{L}+/u,
        (month) => month.charAt(0).toUpperCase() + month.slice(1),
      );
    },
  },
};
</script>
